import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

const AuthRedirectHandler: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    const handleRedirect = async () => {
      const hash = window.location.hash;
      if (!hash) return;

      const params = new URLSearchParams(hash.substring(1));
      const access_token = params.get('access_token');
      const refresh_token = params.get('refresh_token');
      const type = params.get('type');

      console.log('[RedirectHandler] type:', type);
      console.log('[RedirectHandler] token:', access_token);

      if (!access_token) return;

      try {
        setProcessing(true);

        const { error } = await supabase.auth.setSession({
          access_token,
          refresh_token: refresh_token || '',
        });

        if (error) {
          console.error('[RedirectHandler] setSession error:', error.message);
          navigate('/');
          return;
        }

        if (type === 'recovery') {
          navigate('/reset-password', { replace: true });
          return;
        }

        const { data: { user } } = await supabase.auth.getUser();

        if (!user) {
          navigate('/');
          return;
        }

        const { data: profile } = await supabase
          .from('profiles')
          .select('id, user_type, admin_user, approved')
          .eq('id', user.id)
          .maybeSingle();

        if (!profile) {
          const displayName = user.user_metadata?.full_name || user.email?.split('@')[0] || 'User';

          const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-user-profile`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            },
            body: JSON.stringify({
              userId: user.id,
              displayName: displayName,
              userType: 'client',
              agreedToTerms: true,
              termsAcceptedAt: new Date().toISOString()
            }),
          });

          if (!response.ok) {
            console.error('Failed to create profile');
          }

          navigate('/client/dashboard', { replace: true });
          return;
        }

        if (profile.admin_user) {
          navigate('/admin/dashboard', { replace: true });
        } else if (profile.user_type === 'advisor') {
          if (profile.approved) {
            navigate('/advisor/dashboard', { replace: true });
          } else {
            navigate('/', { replace: true });
          }
        } else {
          navigate('/client/dashboard', { replace: true });
        }
      } catch (err) {
        console.error('[RedirectHandler] unexpected error:', err);
        navigate('/');
      } finally {
        setProcessing(false);
      }
    };

    handleRedirect();
  }, [location, navigate]);

  if (processing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-purple-50">
        <div className="text-center text-purple-700 text-xl font-bold animate-pulse">
          Loading...
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default AuthRedirectHandler;
